import { capitalise, capitaliseFirstLetterOnly } from 'lib/string/stringUtils'
import { createSelector } from 'reselect'
import getInsuranceItemsView from './getInsuranceItemsView'
import moment from 'moment'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { checkoutContainsFlighsWithEmbeddedInsurance, getFlightItems } from './flights'
import { isCheckoutPostPurchaseInsurance } from './insurance'
import { isStandaloneFlight } from 'checkout/selectors/flightSearchSelectors'
import { getCheckoutRouteFromURL } from 'checkout/lib/utils/misc/steps'
import { CheckoutPageId } from 'checkout/constants/pages'
import { getLuxVerticals } from 'lib/insurance/insuranceUtils'

function formatPolicyName(policyName: string, isPostPurchase: boolean) {
  return isPostPurchase ? policyName.replace(/Comprehensive\s*/g, '') : policyName
}

function getInsuranceBreakdownItem(item: App.Checkout.InsuranceItemView, isPostPurchase: boolean): App.Checkout.InsuranceItemBreakdownView {
  return {
    itemId: item.item.itemId,
    title: item.policyNames.length ? '' : item.name,
    price: item.totals.price,
    memberPrice: item.totals.memberPrice,
    itemType: 'insurance',
    additionalInfo: [
      ...item.policyNames.map<App.Checkout.ItemBreakdownViewAdditionalText>(policyName => ({ text: formatPolicyName(policyName, isPostPurchase) })),
      { text: `${moment(item.startDate).format(DMY_CASUAL_FORMAT)} - ${moment(item.endDate).format(DMY_CASUAL_FORMAT)}` },
      { text: `Covered in: ${item.destinationCountries.join(', ')}` },
    ],
    additionalElements: [],
    taxesAndFees: item.totals.taxesAndFees,
    mobileAppPrice: item.mobileAppPrice,
  }
}

interface FormatInsuredFlightsTextParams {
  containsFlighsWithEmbeddedInsurance: boolean;
  flights: Array<App.Checkout.FlightItem>;
  isStandaloneFlightPurchase: boolean;
  checkoutRoute: string | undefined;
}

interface getCFMRAdditionalInfoTextParams extends FormatInsuredFlightsTextParams {
  verticalsInCheckout: Array<string>;
  hasFullBundleCoverage: boolean | undefined;
}

function formatInsuredFlightsText({
  containsFlighsWithEmbeddedInsurance,
  flights,
  isStandaloneFlightPurchase,
  checkoutRoute,
}: FormatInsuredFlightsTextParams) {
  if (!containsFlighsWithEmbeddedInsurance || !flights.length) return
  const flight = flights[0]

  const flightsWithInsurance = flight.flights.reduce((acc, flight, index) => {
    if (flight.fareFamily?.withEmbeddedInsurance) {
      acc.add(index + 1)
    }
    return acc
  }, new Set<number>())

  const totalFlights = flight.flights.length
  const insuredFlights = [...flightsWithInsurance]

  if (insuredFlights.length === totalFlights && (isStandaloneFlightPurchase || checkoutRoute === CheckoutPageId.Purchase || totalFlights > 1)) {
    return 'all your flights'
  }

  if (insuredFlights.length === 1) {
    return `flight ${insuredFlights[0]}`
  }

  if (insuredFlights.length === 2) {
    return `flight ${insuredFlights[0]} and flight ${insuredFlights[1]}`
  }

  // More than 2 flights
  const lastFlight = insuredFlights.pop()
  return `(flight ${insuredFlights.join(', flight ')}, and flight ${lastFlight})`
}

function getCFMRAdditionalInfoText({
  containsFlighsWithEmbeddedInsurance,
  flights,
  isStandaloneFlightPurchase,
  hasFullBundleCoverage,
  checkoutRoute,
  verticalsInCheckout,
}: getCFMRAdditionalInfoTextParams) {
  if (containsFlighsWithEmbeddedInsurance) {
    const flightsText = formatInsuredFlightsText({ containsFlighsWithEmbeddedInsurance, flights, isStandaloneFlightPurchase, checkoutRoute })
    if (!flightsText) return []

    return hasFullBundleCoverage ?
        [verticalsInCheckout.map(vertical => capitaliseFirstLetterOnly(vertical)).join(', ')] :
        [`Cancellation Protection included with ${flightsText} – 100% refund for unforeseen events`]
  }

  return []
}

const getInsuranceBreakdownView = createSelector(
  (state: App.State) => getInsuranceItemsView(state),
  (state: App.State) => state.geo.insuranceProductName,
  (state: App.State) => checkoutContainsFlighsWithEmbeddedInsurance(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => isCheckoutPostPurchaseInsurance(state),
  (state: App.State) => isStandaloneFlight(state),
  (state: App.State) => getCheckoutRouteFromURL(state.router.location.pathname),
  (state: App.State) => getLuxVerticals(state.checkout.cart.items),
  (viewWithStatus, insuranceName, containsFlighsWithEmbeddedInsurance, flights, isPostPurchase, isStandaloneFlightPurchase, checkoutRoute, verticalsInCheckout): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (viewWithStatus.data.length === 0) {
      return {
        hasRequiredData: viewWithStatus.hasRequiredData,
        data: [],
      }
    }

    const breakdownViews = viewWithStatus.data.map((view): App.Checkout.PriceBreakdownView => {
      const breakdownItem = getInsuranceBreakdownItem(view, isPostPurchase)
      if (view.item.insuranceType === 'cfmr') {
        return {
          title: containsFlighsWithEmbeddedInsurance && !view.item.hasFullBundleCoverage ? 'Luxury Escapes Protect' : 'Cancellation ' + capitalise(insuranceName),
          price: view.totals.price + (view.totals.mobileAppDiscount?.amount ?? 0),
          memberPrice: view.totals.memberPrice,
          additionalInfoText: getCFMRAdditionalInfoText({
            containsFlighsWithEmbeddedInsurance,
            flights,
            isStandaloneFlightPurchase,
            hasFullBundleCoverage: view.item.hasFullBundleCoverage,
            checkoutRoute,
            verticalsInCheckout,
          }),
          items: [breakdownItem],
          breakdownItemType: 'bookingProtection',
          alwaysShowItemPrice: !!view.totals.price,
          mobileAppDiscount: view.totals.mobileAppDiscount,
        }
      } else {
        return {
          title: 'Travel ' + capitalise(insuranceName),
          price: view.totals.price + (view.totals.mobileAppDiscount?.amount ?? 0),
          memberPrice: view.totals.memberPrice,
          additionalInfoText: [],
          items: [breakdownItem],
          breakdownItemType: 'insurance',
          mobileAppDiscount: view.totals.mobileAppDiscount,
        }
      }
    })
    return {
      hasRequiredData: viewWithStatus.hasRequiredData,
      data: breakdownViews,
    }
  },
)

export default getInsuranceBreakdownView
